import { threatMonitor } from '@services/security/threatMonitor';
import { inputValidator } from '@utils/validation/inputValidator';

export interface WikipediaPage {
  id: number;
  title: string;
  extract: string;
  content?: string; // For legacy compatibility
  image?: string; // For legacy compatibility
  thumbnail?: {
    source: string;
    width: number;
    height: number;
  };
  coordinates?: {
    lat: number;
    lon: number;
  };
  links: WikipediaLink[];
  categories: string[];
  langlinks?: LanguageLink[];
  pageviews?: number;
  lastModified: string;
}

export interface WikipediaLink {
  title: string;
  exists: boolean;
  namespace: number;
  displayTitle?: string;
}

export interface LanguageLink {
  lang: string;
  title: string;
  url: string;
}

export interface SearchResult {
  title: string;
  snippet: string;
  timestamp: string;
  wordcount: number;
  size: number;
}

export interface WikipediaServiceConfig {
  baseURL: string;
  userAgent: string;
  timeout: number;
  maxRetries: number;
  rateLimitDelay: number;
  cacheExpiry: number;
}

export interface RequestOptions {
  useCache?: boolean;
  timeout?: number;
  priority?: 'low' | 'normal' | 'high';
  source?: string;
}

class WikipediaService {
  private config: WikipediaServiceConfig;
  private requestQueue: Map<string, Promise<any>> = new Map();
  private cache: Map<string, { data: any; expires: number }> = new Map();
  private rateLimitTracker: Map<string, number[]> = new Map();
  private retryDelays = [1000, 2000, 4000, 8000];

  constructor() {
    this.config = {
      baseURL: 'https://en.wikipedia.org/w/api.php',
      userAgent: 'BlueLinks/1.0 (Educational Game)',
      timeout: 10000,
      maxRetries: 3,
      rateLimitDelay: 100,
      cacheExpiry: 300000 // 5 minutes
    };
  }

  /**
   * Get a Wikipedia page by title
   */
  async getPage(title: string, options: RequestOptions = {}): Promise<WikipediaPage> {
    // Validate input
    const validation = inputValidator.validateInput(title, {
      required: true,
      maxLength: 256,
      stripWhitespace: true
    });

    if (!validation.isValid) {
      throw new Error(`Invalid page title: ${validation.errors.join(', ')}`);
    }

    const sanitizedTitle = validation.sanitizedValue!;
    const cacheKey = `page:${sanitizedTitle}`;

    // Check cache first
    if (options.useCache !== false) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    // Check for duplicate requests
    if (this.requestQueue.has(cacheKey)) {
      return this.requestQueue.get(cacheKey)!;
    }

    const requestPromise = this.fetchPageData(sanitizedTitle, options);
    this.requestQueue.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache successful result
      if (options.useCache !== false) {
        this.setCache(cacheKey, result);
      }

      this.logRequest('get_page', sanitizedTitle, 'success', options);
      return result;

    } catch (error) {
      this.logRequest('get_page', sanitizedTitle, 'error', options, error);
      throw error;
    } finally {
      this.requestQueue.delete(cacheKey);
    }
  }

  /**
   * Search Wikipedia articles
   */
  async search(query: string, limit: number = 10, options: RequestOptions = {}): Promise<SearchResult[]> {
    // Validate input
    const validation = inputValidator.validateInput(query, {
      required: true,
      maxLength: 300,
      stripWhitespace: true
    });

    if (!validation.isValid) {
      throw new Error(`Invalid search query: ${validation.errors.join(', ')}`);
    }

    if (limit < 1 || limit > 50) {
      throw new Error('Search limit must be between 1 and 50');
    }

    const sanitizedQuery = validation.sanitizedValue!;
    const cacheKey = `search:${sanitizedQuery}:${limit}`;

    // Check cache
    if (options.useCache !== false) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    // Check for duplicate requests
    if (this.requestQueue.has(cacheKey)) {
      return this.requestQueue.get(cacheKey)!;
    }

    const params = new URLSearchParams({
      action: 'query',
      format: 'json',
      origin: '*',
      list: 'search',
      srsearch: sanitizedQuery,
      srlimit: limit.toString(),
      srprop: 'snippet|titlesnippet|size|wordcount|timestamp'
    });

    const requestPromise = this.makeRequest(params, options);
    this.requestQueue.set(cacheKey, requestPromise);

    try {
      const response = await requestPromise;
      const results: SearchResult[] = response.query.search.map((item: any) => ({
        title: item.title,
        snippet: this.cleanSnippet(item.snippet),
        timestamp: item.timestamp,
        wordcount: item.wordcount,
        size: item.size
      }));

      // Cache results
      if (options.useCache !== false) {
        this.setCache(cacheKey, results);
      }

      this.logRequest('search', sanitizedQuery, 'success', options);
      return results;

    } catch (error) {
      this.logRequest('search', sanitizedQuery, 'error', options, error);
      throw error;
    } finally {
      this.requestQueue.delete(cacheKey);
    }
  }

  /**
   * Get random Wikipedia page
   */
  async getRandomPage(options: RequestOptions = {}): Promise<WikipediaPage> {
    const params = new URLSearchParams({
      action: 'query',
      format: 'json',
      origin: '*',
      list: 'random',
      rnnamespace: '0',
      rnlimit: '1'
    });

    try {
      const response = await this.makeRequest(params, options);
      const randomTitle = response.query.random[0].title;
      
      this.logRequest('random_page', randomTitle, 'success', options);
      return this.getPage(randomTitle, options);

    } catch (error) {
      this.logRequest('random_page', 'unknown', 'error', options, error);
      throw error;
    }
  }

  /**
   * Get page links for navigation
   */
  async getPageLinks(title: string, limit: number = 50, options: RequestOptions = {}): Promise<WikipediaLink[]> {
    // Validate input
    const validation = inputValidator.validateInput(title, {
      required: true,
      maxLength: 256,
      stripWhitespace: true
    });

    if (!validation.isValid) {
      throw new Error(`Invalid page title: ${validation.errors.join(', ')}`);
    }

    if (limit < 1 || limit > 500) {
      throw new Error('Links limit must be between 1 and 500');
    }

    const sanitizedTitle = validation.sanitizedValue!;
    const cacheKey = `links:${sanitizedTitle}:${limit}`;

    // Check cache
    if (options.useCache !== false) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    const params = new URLSearchParams({
      action: 'query',
      format: 'json',
      origin: '*',
      titles: sanitizedTitle,
      prop: 'links',
      pllimit: limit.toString(),
      plnamespace: '0'
    });

    try {
      const response = await this.makeRequest(params, options);
      const pages = response.query?.pages;
      if (!pages) {
        throw new Error('Invalid response from Wikipedia API');
      }
      
      const pageId = Object.keys(pages)[0];
      if (!pageId) {
        throw new Error(`Page not found: ${sanitizedTitle}`);
      }
      
      const links = pages[pageId]?.links || [];

      const result: WikipediaLink[] = links.map((link: any) => ({
        title: link.title,
        exists: !link.missing,
        namespace: link.ns || 0,
        displayTitle: link.title
      }));

      // Cache results
      if (options.useCache !== false) {
        this.setCache(cacheKey, result);
      }

      this.logRequest('get_links', sanitizedTitle, 'success', options);
      return result;

    } catch (error) {
      this.logRequest('get_links', sanitizedTitle, 'error', options, error);
      throw error;
    }
  }

  /**
   * Check if pages exist
   */
  async pagesExist(titles: string[], options: RequestOptions = {}): Promise<Map<string, boolean>> {
    if (titles.length === 0) {
      return new Map();
    }

    if (titles.length > 50) {
      throw new Error('Cannot check more than 50 pages at once');
    }

    // Validate all titles
    const validatedTitles: string[] = [];
    for (const title of titles) {
      const validation = inputValidator.validateInput(title, {
        required: true,
        maxLength: 256,
        stripWhitespace: true
      });

      if (validation.isValid && validation.sanitizedValue) {
        validatedTitles.push(validation.sanitizedValue);
      }
    }

    const cacheKey = `exists:${validatedTitles.sort().join('|')}`;

    // Check cache
    if (options.useCache !== false) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;
    }

    const params = new URLSearchParams({
      action: 'query',
      format: 'json',
      origin: '*',
      titles: validatedTitles.join('|'),
      prop: 'info'
    });

    try {
      const response = await this.makeRequest(params, options);
      const pages = response.query.pages;
      const result = new Map<string, boolean>();

      validatedTitles.forEach(title => {
        result.set(title, false);
      });

      Object.values(pages).forEach((page: any) => {
        if (page.title && !page.missing) {
          result.set(page.title, true);
        }
      });

      // Cache results
      if (options.useCache !== false) {
        this.setCache(cacheKey, result);
      }

      this.logRequest('pages_exist', validatedTitles.join(','), 'success', options);
      return result;

    } catch (error) {
      this.logRequest('pages_exist', validatedTitles.join(','), 'error', options, error);
      throw error;
    }
  }

  /**
   * Clear service cache
   */
  clearCache(): void {
    this.cache.clear();
    this.logRequest('clear_cache', 'all', 'success');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    entries: number;
    hitRate: number;
    memoryUsage: number;
  } {
    const now = Date.now();
    let validEntries = 0;
    let memoryUsage = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.expires > now) {
        validEntries++;
        memoryUsage += JSON.stringify(entry.data).length + key.length;
      }
    }

    return {
      size: this.cache.size,
      entries: validEntries,
      hitRate: 0, // TODO: Implement hit rate tracking
      memoryUsage
    };
  }

  /**
   * Fetch page data with full details
   */
  private async fetchPageData(title: string, options: RequestOptions): Promise<WikipediaPage> {
    const params = new URLSearchParams({
      action: 'query',
      format: 'json',
      origin: '*',
      titles: title,
      prop: 'extracts|pageimages|coordinates|links|categories|langlinks|pageviews',
      exintro: 'true',
      explaintext: 'true',
      exsectionformat: 'plain',
      piprop: 'thumbnail',
      pithumbsize: '300',
      pllimit: '50',
      plnamespace: '0',
      cllimit: '50',
      llprop: 'url',
      lllimit: '10',
      pvipdays: '30'
    });

    const response = await this.makeRequest(params, options);
    const pages = response.query?.pages;
    if (!pages) {
      throw new Error('Invalid response from Wikipedia API');
    }
    
    const pageId = Object.keys(pages)[0];
    if (!pageId) {
      throw new Error(`Page not found: ${title}`);
    }
    
    const pageData = pages[pageId];
    if (!pageData) {
      throw new Error(`Page not found: ${title}`);
    }


    if (pageData.missing) {
      throw new Error(`Page not found: ${title}`);
    }

    const result: WikipediaPage = {
      id: parseInt(pageId, 10),
      title: pageData.title || '',
      extract: pageData.extract || '',
      links: (pageData.links || []).map((link: any) => ({
        title: link.title,
        exists: !link.missing,
        namespace: link.ns || 0,
        displayTitle: link.title
      })),
      categories: (pageData.categories || []).map((cat: any) => cat.title),
      pageviews: pageData.pageviews?.['*'] || 0,
      lastModified: new Date().toISOString()
    };

    // Add optional properties only if they exist
    if (pageData.thumbnail?.source) {
      result.thumbnail = {
        source: pageData.thumbnail.source,
        width: pageData.thumbnail.width || 0,
        height: pageData.thumbnail.height || 0
      };
    }

    if (pageData.coordinates?.[0]) {
      result.coordinates = {
        lat: pageData.coordinates[0].lat || 0,
        lon: pageData.coordinates[0].lon || 0
      };
    }

    if (pageData.langlinks) {
      result.langlinks = pageData.langlinks.map((ll: any) => ({
        lang: ll.lang || '',
        title: ll.title || '',
        url: ll.url || ''
      }));
    }

    return result;
  }

  /**
   * Make HTTP request with rate limiting and retry logic
   */
  private async makeRequest(params: URLSearchParams, options: RequestOptions): Promise<any> {
    await this.enforceRateLimit(options.source || 'default');

    const url = `${this.config.baseURL}?${params.toString()}`;
    const timeout = options.timeout ?? this.config.timeout;

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.config.maxRetries; attempt++) {
      if (attempt > 0) {
        const delay = this.retryDelays[Math.min(attempt - 1, this.retryDelays.length - 1)] || 1000;
        await this.delay(delay);
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout || 10000);

        const response = await fetch(url, {
          headers: {
            'User-Agent': this.config.userAgent
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(`Wikipedia API Error: ${data.error.info}`);
        }

        return data;

      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        if (error instanceof Error && error.name === 'AbortError') {
          lastError = new Error('Request timeout');
        }

        // Don't retry on certain errors
        if (lastError.message.includes('404') || lastError.message.includes('Invalid')) {
          break;
        }
      }
    }

    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * Enforce rate limiting
   */
  private async enforceRateLimit(source: string): Promise<void> {
    const now = Date.now();
    const requests = this.rateLimitTracker.get(source) || [];
    
    // Clean old requests (older than 1 minute)
    const recentRequests = requests.filter(timestamp => now - timestamp < 60000);
    
    // Check if we need to wait
    if (recentRequests.length >= 30) { // 30 requests per minute
      const oldestRecent = Math.min(...recentRequests);
      const waitTime = 60000 - (now - oldestRecent);
      
      if (waitTime > 0) {
        await this.delay(waitTime);
        return this.enforceRateLimit(source);
      }
    }

    // Add current request
    recentRequests.push(now);
    this.rateLimitTracker.set(source, recentRequests);

    // Add small delay between requests
    if (recentRequests.length > 1) {
      await this.delay(this.config.rateLimitDelay);
    }
  }

  /**
   * Get data from cache
   */
  private getFromCache(key: string): any | null {
    const entry = this.cache.get(key);
    if (entry && entry.expires > Date.now()) {
      return entry.data;
    }
    
    if (entry) {
      this.cache.delete(key);
    }
    
    return null;
  }

  /**
   * Set data in cache
   */
  private setCache(key: string, data: any): void {
    const expires = Date.now() + this.config.cacheExpiry;
    this.cache.set(key, { data, expires });

    // Clean expired entries periodically
    if (this.cache.size > 1000) {
      this.cleanExpiredCache();
    }
  }

  /**
   * Clean expired cache entries
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expires <= now) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clean HTML snippets
   */
  private cleanSnippet(snippet: string): string {
    return snippet
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Log service requests for monitoring
   */
  private logRequest(
    operation: string, 
    target: string, 
    status: 'success' | 'error', 
    options?: RequestOptions, 
    error?: any
  ): void {
    const severity = status === 'error' ? 'medium' : 'low';
    
    threatMonitor.logEvent({
      type: 'system_error',
      severity,
      source: 'wikipedia_service',
      details: {
        operation,
        target,
        status,
        priority: options?.priority || 'normal',
        source: options?.source || 'default',
        error: error?.message,
        cacheUsed: options?.useCache !== false
      }
    });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.cache.clear();
    this.requestQueue.clear();
    this.rateLimitTracker.clear();
  }
}

// Export singleton instance
export const wikipediaService = new WikipediaService();
export default wikipediaService;